import type React from 'react';
import { CryptoIcon, FiatFlag, ChevronDown } from '../ui/icons';
import { useT } from '../../i18n';
import type { FieldLocksConfig } from '@nowramp/sdk';

type MaybeFieldLocks = FieldLocksConfig | null | undefined;

export interface SpendCardProps {
  amount: string;
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currencyCode: string;
  isBuy: boolean;
  onOpenModal: () => void;
  assetBaseUrl: string;
  error?: string;
  fieldLocks?: MaybeFieldLocks;
  onBlur: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
  /** Chain code for sell mode crypto display */
  chainCode?: string;
  chainDisplay?: string;
  getChainIconUrl?: (chain: string, base: string) => string;
  shaking?: boolean;
  onFocus?: () => void;
}

export function SpendCard({
  amount,
  onAmountChange,
  currencyCode,
  isBuy,
  onOpenModal,
  assetBaseUrl,
  error,
  fieldLocks,
  onBlur,
  inputRef,
  chainCode,
  chainDisplay,
  getChainIconUrl,
  shaking,
  onFocus,
}: SpendCardProps) {
  const t = useT();
  return (
    <>
      <div
        className={`flex flex-col gap-[20px] p-4 card-hover${shaking ? ' error-shake' : ''}`}
        style={{
          background: 'var(--t-surface)',
          borderRadius: 'var(--t-card-radius)',
          ...(error ? { border: '1px solid var(--t-error)' } : {}),
        }}
      >
        <div className="text-[16px]" style={{ color: 'var(--t-text-secondary)', lineHeight: '16px' }}>
          {t('spend.label')}
        </div>
        <div className="flex items-center justify-between">
          <input
            ref={inputRef}
            type="text"
            value={amount}
            onChange={onAmountChange}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={isBuy ? t('spend.placeholderFiat') : t('spend.placeholderCrypto')}
            className="bg-transparent outline-none w-0 flex-1 mr-3"
            style={{
              color: 'var(--t-text)',
              fontSize: 24,
              fontWeight: 500,
              lineHeight: '32px',
              padding: 0,
              border: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              height: 'auto',
              width: 0,
            }}
            inputMode="decimal"
            disabled={!!fieldLocks?.sourceAmount?.locked}
          />
          {isBuy ? (
            <button
              onClick={onOpenModal}
              className="flex items-center gap-[4px] shrink-0 transition-colors"
              style={{
                background: 'var(--t-pill-solid-bg)',
                borderRadius: 'var(--t-pill-radius)',
                paddingLeft: 4,
                paddingRight: 8,
                paddingTop: 4,
                paddingBottom: 4,
                border: '1px solid var(--t-pill-solid-border)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
              }}
              disabled={!!fieldLocks?.sourceCurrency?.locked}
            >
              <FiatFlag currencyCode={currencyCode} size={24} baseUrl={assetBaseUrl} />
              <span className="text-[14px] font-semibold" style={{ color: 'var(--t-text)', letterSpacing: '0.14px', lineHeight: '24px' }}>
                {currencyCode}
              </span>
              <ChevronDown />
            </button>
          ) : (
            <div className="flex flex-col items-end shrink-0">
              <button
                onClick={onOpenModal}
                className="flex items-center gap-[4px] transition-colors"
                style={{
                  background: 'var(--t-pill-solid-bg)',
                  borderRadius: 'var(--t-pill-radius)',
                  paddingLeft: 4,
                  paddingRight: 8,
                  paddingTop: 4,
                  paddingBottom: 4,
                  border: '1px solid var(--t-pill-solid-border)',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)',
                }}
                disabled={!!fieldLocks?.destinationCurrency?.locked}
              >
                <CryptoIcon ticker={currencyCode} size={24} baseUrl={assetBaseUrl} />
                <span className="text-[14px] font-semibold" style={{ color: 'var(--t-text)', letterSpacing: '0.14px', lineHeight: '24px' }}>
                  {currencyCode}
                </span>
                <ChevronDown />
              </button>
              {chainCode && getChainIconUrl && (
                <div className="flex items-center gap-1 mt-1.5 mr-0.5">
                  <div className="rounded-full" style={{ width: 14, height: 14, background: '#ffffff' }}>
                    <img
                      src={getChainIconUrl(chainCode, assetBaseUrl)}
                      alt={chainCode}
                      width={14}
                      height={14}
                      className="rounded-full"
                      style={{ width: 14, height: 14 }}
                    />
                  </div>
                  <span className="text-[12px]" style={{ color: 'var(--t-text-secondary)' }}>
                    {chainDisplay}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {error && (
        <div className="text-[12px] mt-1.5 text-center" style={{ color: 'var(--t-error)' }}>
          {error}
        </div>
      )}
    </>
  );
}
